import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "row", "customTab", "customPanel",
    "startMonth", "startYear", "endMonth", "endYear",
    "userFilter", "coachFilter", "tableBody",
    "lessonGroup", "lessonGroups",
    "lessonTotal", "lessonPaid", "lessonOutstanding",
    "groupCheckbox"
  ]
  static values = { url: String, feesPath: String }

  connect() {
    this.lessonGroupTargets.forEach(group => {
      this.updateGroupCheckboxState(group)
    })
  }

  toggleCustomRange() {
    this.customPanelTarget.classList.toggle("uk-hidden")
  }

  filterUsers() {
    const query = this.userFilterTarget.value.toLowerCase().trim()

    this.rowTargets.forEach(row => {
      const name = row.dataset.userName || ""
      row.style.display = (!query || name.includes(query)) ? "" : "none"
    })

    if (this.hasLessonGroupTarget) {
      this.filterLessonRows()
    }
  }

  filterLessonRows() {
    const dancerQuery = this.hasUserFilterTarget ? this.userFilterTarget.value.toLowerCase().trim() : ""
    const coachQuery = this.hasCoachFilterTarget ? this.coachFilterTarget.value.toLowerCase().trim() : ""

    this.lessonGroupTargets.forEach(group => {
      const dancerName = group.dataset.dancerName || ""
      const coaches = group.dataset.coaches || ""

      const dancerMatch = !dancerQuery || dancerName.includes(dancerQuery)
      const coachMatch = !coachQuery || coaches.includes(coachQuery)

      group.style.display = (dancerMatch && coachMatch) ? "" : "none"

      if (dancerMatch && coachMatch) {
        group.querySelectorAll(".lesson-detail-row").forEach(row => {
          const rowCoach = row.dataset.coachName || ""
          const matchesCoach = !coachQuery || rowCoach.includes(coachQuery)
          row.style.display = matchesCoach ? "" : "none"
        })
      }
    })

    this.recalculateTotals()
  }

  get currencyUnit() {
    if (this.hasLessonGroupsTarget) {
      return this.lessonGroupsTarget.dataset.currencyUnit || "PLN "
    }
    return "PLN "
  }

  recalculateTotals() {
    let visibleTotal = 0
    let visiblePaid = 0

    this.lessonGroupTargets.forEach(group => {
      if (group.style.display === "none") return

      group.querySelectorAll(".lesson-detail-row").forEach(row => {
        if (row.style.display === "none") return
        const amount = parseFloat(row.dataset.amount) || 0
        visibleTotal += amount
        const checkbox = row.querySelector(".lesson-paid-checkbox")
        if (checkbox && checkbox.checked) visiblePaid += amount
      })
    })

    const outstanding = visibleTotal - visiblePaid
    const unit = this.currencyUnit

    if (this.hasLessonTotalTarget) {
      this.lessonTotalTarget.textContent = this.formatCurrency(visibleTotal, unit)
    }
    if (this.hasLessonPaidTarget) {
      this.lessonPaidTarget.textContent = this.formatCurrency(visiblePaid, unit)
    }
    if (this.hasLessonOutstandingTarget) {
      this.lessonOutstandingTarget.textContent = this.formatCurrency(outstanding, unit)
    }
  }

  formatCurrency(amount, unit) {
    return `${unit}${amount.toFixed(2)}`
  }

  toggleGroup(event) {
    if (event.target.closest(".group-checkbox-wrapper")) return

    const group = event.currentTarget.closest(".lesson-dancer-group")
    if (!group) return
    group.classList.toggle("is-open")
  }

  applyRange() {
    const sm = this.startMonthTarget.value.padStart(2, "0")
    const sy = this.startYearTarget.value
    const em = this.endMonthTarget.value.padStart(2, "0")
    const ey = this.endYearTarget.value

    const rangeStart = `${sy}-${sm}-01`
    const rangeEnd = `${ey}-${em}-01`

    const url = new URL(this.feesPathValue, window.location.origin)
    url.searchParams.set("range_start", rangeStart)
    url.searchParams.set("range_end", rangeEnd)

    const currentFilter = new URLSearchParams(window.location.search).get("filter") || "all"
    url.searchParams.set("filter", currentFilter)

    const currentFeeType = new URLSearchParams(window.location.search).get("fee_type") || "membership"
    url.searchParams.set("fee_type", currentFeeType)

    window.location.href = url.toString()
  }

  async toggle(event) {
    const checkbox = event.target
    const userId = event.params.user
    const month = event.params.month

    checkbox.disabled = true

    try {
      const response = await this.postToggle({ user_id: userId, month: month })
      const data = await response.json()
      checkbox.checked = data.paid
    } catch {
      checkbox.checked = !checkbox.checked
    } finally {
      checkbox.disabled = false
    }
  }

  async toggleLesson(event) {
    const checkbox = event.target
    const userId = event.params.user
    const lessonId = event.params.lesson
    const date = event.params.date

    checkbox.disabled = true
    const wasPaid = !checkbox.checked

    try {
      const response = await this.postToggle({
        user_id: userId, lesson_id: lessonId, date: date
      })
      const data = await response.json()
      checkbox.checked = data.paid
    } catch {
      checkbox.checked = wasPaid
    } finally {
      checkbox.disabled = false
      this.recalculateTotals()
      this.updateGroupCheckboxState(checkbox.closest(".lesson-dancer-group"))
    }
  }

  async toggleGroupPayment(event) {
    const groupCheckbox = event.target
    const group = groupCheckbox.closest(".lesson-dancer-group")
    if (!group) return

    const shouldPay = groupCheckbox.checked
    const checkboxes = group.querySelectorAll(".lesson-paid-checkbox")
    const toToggle = Array.from(checkboxes).filter(cb => cb.checked !== shouldPay)

    if (toToggle.length === 0) return

    groupCheckbox.disabled = true
    toToggle.forEach(cb => { cb.disabled = true })

    try {
      const results = await Promise.allSettled(
        toToggle.map(cb => {
          const row = cb.closest("tr")
          return this.postToggle({
            user_id: cb.dataset.feeJournalUserParam,
            lesson_id: cb.dataset.feeJournalLessonParam,
            date: cb.dataset.feeJournalDateParam
          }).then(r => r.json()).then(data => {
            cb.checked = data.paid
          }).catch(() => {
            cb.checked = !shouldPay
          })
        })
      )
    } finally {
      toToggle.forEach(cb => { cb.disabled = false })
      groupCheckbox.disabled = false
      this.recalculateTotals()
      this.updateGroupCheckboxState(group)
    }
  }

  updateGroupCheckboxState(group) {
    if (!group) return
    const groupCb = group.querySelector(".group-select-all")
    if (!groupCb) return

    const checkboxes = group.querySelectorAll(".lesson-paid-checkbox")
    const total = checkboxes.length
    const checked = Array.from(checkboxes).filter(cb => cb.checked).length

    groupCb.checked = total > 0 && checked === total
    groupCb.indeterminate = checked > 0 && checked < total
  }

  async postToggle(body) {
    const token = document.querySelector('meta[name="csrf-token"]')?.content
    const response = await fetch(this.urlValue, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": token,
        "Accept": "application/json",
        "X-Requested-With": "XMLHttpRequest"
      },
      body: JSON.stringify(body)
    })
    if (!response.ok) throw new Error("Request failed")
    return response
  }
}
